import { EventEmitter } from '../utils/event-emitter.js';
import { querySelector, querySelectorAll } from '../utils/helpers.js';
import {
  SELECTORS,
  CSS_CLASSES,
  EVENTS,
  SORT_TYPES,
  SORT_ORDERS,
  DEFAULTS,
} from '../utils/constants.js';

/**
 * Менеджер сортировки
 * Отвечает за управление сортировкой фильмов и UI элементами сортировки
 */
export class SortManager extends EventEmitter {
  constructor() {
    super();
    this.sortButton = null;
    this.sortOrderButton = null;
    this.sortDropdown = null;
    this.sortOptions = null;

    this.currentSortType = DEFAULTS.SORT_TYPE;
    this.currentSortOrder = DEFAULTS.SORT_ORDER;

    this.isDropdownOpen = false;
    this.eventsInitialized = false;
  }

  /**
   * Инициализация менеджера
   */
  init() {
    this.sortButton = querySelector(SELECTORS.SORT_BUTTON);
    this.sortOrderButton = querySelector(SELECTORS.SORT_ORDER_BUTTON);
    this.sortDropdown = querySelector(SELECTORS.SORT_DROPDOWN);
    this.sortOptions = querySelectorAll(SELECTORS.SORT_OPTION);

    if (!this.sortButton || !this.sortOrderButton || !this.sortDropdown) {
      throw new Error('Элементы сортировки не найдены');
    }

    this.setupEventListeners();
    this.updateUI();

    console.log('🔄 SortManager инициализирован');
  }

  /**
   * Настройка обработчиков событий
   */
  setupEventListeners() {
    if (this.eventsInitialized) return;

    // Клик по кнопке сортировки (открытие dropdown)
    this.sortButton?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.toggleDropdown();
    });

    // Клик по кнопке изменения порядка сортировки
    this.sortOrderButton?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.toggleSortOrder();
    });

    // Клики по опциям сортировки
    this.sortOptions?.forEach(option => {
      option.addEventListener('click', (event) => {
        event.stopPropagation();
        const sortType = option.getAttribute('data-sort');
        this.setSortType(sortType);
        this.closeDropdown();
      });
    });

    // Закрытие dropdown при клике вне его
    document.addEventListener('click', (event) => {
      if (this.isDropdownOpen && !this.isClickInsideSort(event.target)) {
        this.closeDropdown();
      }
    });

    // Закрытие dropdown при нажатии Escape
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isDropdownOpen) {
        this.closeDropdown();
      }
    });

    // Навигация по dropdown с клавиатуры
    document.addEventListener('keydown', (event) => {
      if (this.isDropdownOpen) {
        this.handleKeyboardNavigation(event);
      }
    });

    this.eventsInitialized = true;
  }

  /**
   * Проверка, был ли клик внутри области сортировки
   * @param {Element} target - Целевой элемент клика
   * @returns {boolean} - Результат проверки
   */
  isClickInsideSort(target) {
    return (
      this.sortButton?.contains(target) ||
      this.sortOrderButton?.contains(target) ||
      this.sortDropdown?.contains(target)
    );
  }

  /**
   * Переключение состояния dropdown
   */
  toggleDropdown() {
    if (this.isDropdownOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  /**
   * Открытие dropdown
   */
  openDropdown() {
    if (!this.sortDropdown) return;

    this.sortDropdown.classList.add(CSS_CLASSES.SHOW);
    this.isDropdownOpen = true;

    // Добавляем ARIA атрибуты для доступности
    this.sortButton?.setAttribute('aria-expanded', 'true');

    // Фокус на первую опцию
    const firstOption = this.sortDropdown.querySelector(SELECTORS.SORT_OPTION);
    if (firstOption) {
      firstOption.focus();
    }
  }

  /**
   * Закрытие dropdown
   */
  closeDropdown() {
    if (!this.sortDropdown) return;

    this.sortDropdown.classList.remove(CSS_CLASSES.SHOW);
    this.isDropdownOpen = false;

    // Обновляем ARIA атрибуты
    this.sortButton?.setAttribute('aria-expanded', 'false');

    // Возвращаем фокус на кнопку
    this.sortButton?.focus();
  }

  /**
   * Обработка навигации с клавиатуры в dropdown
   * @param {KeyboardEvent} event - Событие клавиатуры
   */
  handleKeyboardNavigation(event) {
    const options = Array.from(this.sortOptions || []);
    const activeOption = document.activeElement;
    const currentIndex = options.indexOf(activeOption);

    switch (event.key) {
    case 'ArrowDown': {
      event.preventDefault();
      const nextIndex = (currentIndex + 1) % options.length;
      options[nextIndex]?.focus();
      break;
    }

    case 'ArrowUp': {
      event.preventDefault();
      const prevIndex = currentIndex <= 0 ? options.length - 1 : currentIndex - 1;
      options[prevIndex]?.focus();
      break;
    }

    case 'Enter':
    case ' ':
      event.preventDefault();
      if (activeOption && options.includes(activeOption)) {
        const sortType = activeOption.getAttribute('data-sort');
        this.setSortType(sortType);
        this.closeDropdown();
      }
      break;

    case 'Tab':
      // Разрешаем Tab для навигации, но закрываем dropdown
      this.closeDropdown();
      break;
    }
  }

  /**
   * Установка типа сортировки
   * @param {string} sortType - Тип сортировки
   */
  setSortType(sortType) {
    if (!Object.values(SORT_TYPES).includes(sortType)) {
      console.warn(`Неизвестный тип сортировки: ${sortType}`);
      return;
    }

    const oldSortType = this.currentSortType;
    this.currentSortType = sortType;

    // Обновляем UI
    this.updateSortTypeUI();

    // Уведомляем об изменении, если тип действительно изменился
    if (oldSortType !== sortType) {
      this.emitSortChange();
      this.emit(EVENTS.SORT_TYPE_CHANGED, sortType);
    }
  }

  /**
   * Переключение порядка сортировки
   */
  toggleSortOrder() {
    const newOrder = this.currentSortOrder === SORT_ORDERS.ASC
      ? SORT_ORDERS.DESC
      : SORT_ORDERS.ASC;

    this.setSortOrder(newOrder);
  }

  /**
   * Установка порядка сортировки
   * @param {string} sortOrder - Порядок сортировки
   */
  setSortOrder(sortOrder) {
    if (!Object.values(SORT_ORDERS).includes(sortOrder)) {
      console.warn(`Неизвестный порядок сортировки: ${sortOrder}`);
      return;
    }

    const oldSortOrder = this.currentSortOrder;
    this.currentSortOrder = sortOrder;

    // Обновляем UI
    this.updateSortOrderUI();

    // Уведомляем об изменении, если порядок действительно изменился
    if (oldSortOrder !== sortOrder) {
      this.emitSortChange();
      this.emit(EVENTS.SORT_ORDER_CHANGED, sortOrder);
    }
  }

  /**
   * Обновление UI после изменения настроек сортировки
   */
  updateUI() {
    this.updateSortTypeUI();
    this.updateSortOrderUI();
  }

  /**
   * Обновление UI типа сортировки
   */
  updateSortTypeUI() {
    // Обновляем текст кнопки сортировки
    if (this.sortButton) {
      const activeOption = this.sortDropdown?.querySelector(`[data-sort="${this.currentSortType}"]`);
      const sortText = activeOption?.textContent || 'Сортировка';
      const textSpan = this.sortButton.querySelector('span:first-child');
      if (textSpan) {
        textSpan.textContent = sortText;
      }
    }

    // Обновляем активную опцию в dropdown
    this.sortOptions?.forEach(option => {
      const sortType = option.getAttribute('data-sort');
      if (sortType === this.currentSortType) {
        option.classList.add(CSS_CLASSES.ACTIVE);
      } else {
        option.classList.remove(CSS_CLASSES.ACTIVE);
      }
    });
  }

  /**
   * Обновление UI порядка сортировки
   */
  updateSortOrderUI() {
    if (!this.sortOrderButton) return;

    // Обновляем иконку и подсказку
    if (this.currentSortOrder === SORT_ORDERS.ASC) {
      this.sortOrderButton.textContent = '▲'; // иконка вверх
      this.sortOrderButton.title = 'По возрастанию (клик для изменения на убывание)';
    } else {
      this.sortOrderButton.textContent = '▼'; // иконка вниз
      this.sortOrderButton.title = 'По убыванию (клик для изменения на возрастание)';
    }
  }

  /**
   * Уведомление об изменении настроек сортировки
   */
  emitSortChange() {
    this.emit(EVENTS.SORT_CHANGED, {
      type: this.currentSortType,
      order: this.currentSortOrder,
    });
  }

  /**
   * Получение текущих настроек сортировки
   * @returns {Object} - Объект с настройками сортировки
   */
  getSortSettings() {
    return {
      type: this.currentSortType,
      order: this.currentSortOrder,
    };
  }

  /**
   * Программная установка настроек сортировки
   * @param {string} sortType - Тип сортировки
   * @param {string} sortOrder - Порядок сортировки
   * @param {boolean} emit - Уведомлять ли об изменении
   */
  setSortSettings(sortType, sortOrder, emit = true) {
    const oldType = this.currentSortType;
    const oldOrder = this.currentSortOrder;

    this.currentSortType = sortType || DEFAULTS.SORT_TYPE;
    this.currentSortOrder = sortOrder || DEFAULTS.SORT_ORDER;

    this.updateUI();

    // Уведомляем об изменении, если настройки изменились
    if (emit && (oldType !== this.currentSortType || oldOrder !== this.currentSortOrder)) {
      this.emitSortChange();
    }
  }

  /**
   * Сброс настроек сортировки к значениям по умолчанию
   */
  resetToDefaults() {
    this.setSortSettings(DEFAULTS.SORT_TYPE, DEFAULTS.SORT_ORDER, true);
  }

  /**
   * Получение всех доступных типов сортировки
   * @returns {Array} - Массив доступных типов
   */
  getAvailableSortTypes() {
    return Object.values(SORT_TYPES);
  }

  /**
   * Получение названия типа сортировки на русском языке
   * @param {string} sortType - Тип сортировки
   * @returns {string} - Название на русском
   */
  getSortTypeName(sortType) {
    const names = {
      [SORT_TYPES.ALPHABET]: 'По алфавиту',
      [SORT_TYPES.CATEGORY]: 'По категории',
      [SORT_TYPES.DURATION]: 'По продолжительности',
      [SORT_TYPES.DATE]: 'По дате',
    };

    return names[sortType] || sortType;
  }

  /**
   * Получение названия порядка сортировки на русском языке
   * @param {string} sortOrder - Порядок сортировки
   * @returns {string} - Название на русском
   */
  getSortOrderName(sortOrder) {
    const names = {
      [SORT_ORDERS.ASC]: 'По возрастанию',
      [SORT_ORDERS.DESC]: 'По убыванию',
    };

    return names[sortOrder] || sortOrder;
  }

  /**
   * Проверка, открыт ли dropdown
   * @returns {boolean} - Состояние dropdown
   */
  isDropdownVisible() {
    return this.isDropdownOpen;
  }

  /**
   * Получение статистики сортировки
   * @returns {Object} - Статистика
   */
  getStatistics() {
    return {
      currentType: this.currentSortType,
      currentOrder: this.currentSortOrder,
      isDropdownOpen: this.isDropdownOpen,
      availableTypes: this.getAvailableSortTypes(),
    };
  }

  /**
   * Деструктор
   */
  destroy() {
    this.closeDropdown();
    this.removeAllListeners();

    this.sortButton = null;
    this.sortOrderButton = null;
    this.sortDropdown = null;
    this.sortOptions = null;
    this.isDropdownOpen = false;
  }
}
